import type {
  BondingCurvePoolQuoteSnapshot,
  BondingCurveTradeGateQuoteSnapshot,
  CpSwapPoolQuoteSnapshot,
  CreatorFeeOn,
  MarketLifecycle,
} from '@morfi/market-math';
import {
  type AccountInfo,
  type Connection,
  type PublicKey,
  SYSVAR_CLOCK_PUBKEY,
} from '@solana/web3.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { deriveOutcomeMintPda } from '../pda/derivations.js';
import { decodeChainClock } from './chain-clock.js';
import { fetchGraduatedPoolInfoByPool } from './fetch.js';
import { decodeMarketAccount } from './market-decode.js';
import {
  graduatedMarketPoolBinding,
  hasVariant,
  validateMarketAddress,
} from './market-pool-binding.js';
import type { MarketPoolRealtimeSnapshot } from './market-pool-realtime-snapshot.js';
import { projectMarketPoolResolutionAt } from './market-pool-resolution.js';
import type { MarketAccount } from './types.js';

export type ChainMarketLifecycle = MarketLifecycle;
export type ChainCreatorFeeOn = CreatorFeeOn;
export type BondingCurvePoolSnapshot = BondingCurvePoolQuoteSnapshot & {
  trackedBondingCurveSoldSupplyD6: string;
};
export type BondingCurveTradeGateSnapshot = BondingCurveTradeGateQuoteSnapshot;
export type CpSwapPoolSnapshot = CpSwapPoolQuoteSnapshot & { pool: string };
export type MarketPoolSnapshot = MarketPoolRealtimeSnapshot;
export type OutcomePoolSnapshot = MarketPoolSnapshot['outcomes'][number];

/** Read current embedded market reserves and economics with one atomic Clock observation. */
export async function fetchMarketPoolSnapshot(
  connection: Connection,
  marketAddress: PublicKey,
  expectedQuoteMint: PublicKey,
  expectedOutcomeCount?: number,
  minimumContextSlot?: number,
): Promise<MarketPoolSnapshot> {
  if (
    expectedOutcomeCount !== undefined &&
    (!Number.isSafeInteger(expectedOutcomeCount) ||
      expectedOutcomeCount < 2 ||
      expectedOutcomeCount > 10)
  ) {
    throw new Error('Expected outcome count must be an integer between 2 and 10');
  }
  if (
    minimumContextSlot !== undefined &&
    (!Number.isSafeInteger(minimumContextSlot) || minimumContextSlot < 0)
  ) {
    throw new Error('minimum context slot must be a non-negative safe integer');
  }
  const program = await getProgram(connection, MORFI_OPINION_PROGRAM_ID);
  const batch = await connection.getMultipleAccountsInfoAndContext(
    [marketAddress, SYSVAR_CLOCK_PUBKEY],
    minimumContextSlot === undefined ? undefined : { minContextSlot: minimumContextSlot },
  );
  const info = accountInfoAt(batch.value, 0, 'Market');
  if (!info.owner.equals(program.programId)) throw new Error('Market has an unexpected owner');
  const market = decodeMarketAccount(program, info.data);
  validateMarketAddress(marketAddress, market);
  if (!market.quoteMint.equals(expectedQuoteMint))
    throw new Error('Market quote mint does not match the expected quote mint');
  if (expectedOutcomeCount !== undefined && expectedOutcomeCount !== market.nOutcomes)
    throw new Error('Expected outcome count does not match the on-chain Market');
  const clock = decodeChainClock(accountInfoAt(batch.value, 1, 'Solana Clock sysvar'));
  if (hasVariant(market.state, 'graduated')) {
    return graduatedSnapshot(
      connection,
      marketAddress,
      market,
      expectedQuoteMint,
      minimumContextSlot,
    );
  }
  return embeddedMarketSnapshot(marketAddress, market, clock);
}

function embeddedMarketSnapshot(
  marketAddress: PublicKey,
  market: MarketAccount,
  clock: ReturnType<typeof decodeChainClock>,
): MarketPoolSnapshot {
  const lifecycle = marketLifecycle(market);
  const trading = lifecycle === 'trading';
  const timed = hasVariant(market.resolutionMode, 'timed');
  const ledgers = market.outcomes.slice(0, market.nOutcomes);
  if (ledgers.length !== market.nOutcomes) throw new Error('Market is missing outcome ledgers');
  const snapshot: MarketPoolSnapshot = {
    market: marketAddress.toBase58(),
    chainSlot: clock.slot.toString(),
    chainUnixTimestampSeconds: clock.unixTimestampSeconds.toString(),
    lifecycle,
    winningOutcomeIndex: market.winningOutcome === 255 ? null : market.winningOutcome,
    bondingCurvesClosed: !trading,
    buyFeeBps: market.feeBpsBuy,
    sellBaseFeeBps: market.feeBpsSell,
    sellPenaltyFeeBps: market.feeBpsSellPenaltyExtra,
    prizePoolFeeBps: market.feeBpsPrizePool,
    minimumQuoteTradeD6: market.minQuoteInPerTrade.toString(),
    bondingCurveTradeGate: trading
      ? {
          outcomeConvictionPpb: ledgers.map((outcome) => outcome.convictionPpb.toString()),
          lastConvictionUpdateUnixSeconds: market.lastConvictionUpdateTs.toString(),
          convictionBaselineReserveQuoteD6: market.convictionBaselineReserveQuote.toString(),
          convictionRetentionPerSecondPpb: market.convictionRetentionPerSecondPpb.toString(),
          convictionResolutionThresholdPpb: market.convictionResolutionThresholdPpb.toString(),
          convictionReferenceMarginPpb: market.convictionReferenceMarginPpb.toString(),
          ...(timed
            ? {
                timedTradingWindow: {
                  startUnixSeconds: market.tradingStartedTs.toString(),
                  endUnixSeconds: market.tradingEndTs.toString(),
                },
              }
            : {}),
          tradingStartedUnixSeconds: market.tradingStartedTs.toString(),
          resolutionThresholdFloorMarginPpb: market.resolutionThresholdFloorMarginPpb.toString(),
          resolutionThresholdDecayDelaySecs: market.resolutionThresholdDecayDelaySecs.toString(),
          resolutionThresholdDropIntervalSecs:
            market.resolutionThresholdDropIntervalSecs.toString(),
          resolutionThresholdDropPpb: market.resolutionThresholdDropPpb.toString(),
        }
      : null,
    outcomes: ledgers.map((outcome, outcomeIndex) => {
      if (outcome.issuedTokens.gt(market.initialTokenSupply))
        throw new Error('Market issued tokens exceed the initial supply');
      return {
        outcomeIndex,
        outcomeMint: deriveOutcomeMintPda(marketAddress, outcomeIndex)[0].toBase58(),
        realQuoteReserveD6: trading ? outcome.quoteReserve.toString() : null,
        bondingCurve: trading
          ? {
              virtualQuoteReserveD6: market.initialVirtualQuote.toString(),
              poolQuoteReserveD6: market.initialVirtualQuote.add(outcome.quoteReserve).toString(),
              initialOutcomeSupplyD6: market.initialTokenSupply.toString(),
              poolOutcomeReserveD6: market.initialTokenSupply.sub(outcome.issuedTokens).toString(),
              trackedBondingCurveSoldSupplyD6: outcome.issuedTokens.toString(),
              closedForTrade: false,
            }
          : null,
        cpSwap: null,
      };
    }),
  };
  if (snapshot.bondingCurveTradeGate !== null && !timed) {
    snapshot.bondingCurveTradeGate.resolutionAtUnixSeconds =
      projectMarketPoolResolutionAt(snapshot);
  }
  return snapshot;
}

async function graduatedSnapshot(
  connection: Connection,
  marketAddress: PublicKey,
  market: MarketAccount,
  expectedQuoteMint: PublicKey,
  minimumContextSlot: number | undefined,
): Promise<MarketPoolSnapshot> {
  const binding = graduatedMarketPoolBinding(marketAddress, market);
  const pool = await fetchGraduatedPoolInfoByPool(connection, {
    poolState: binding.poolState,
    marketAddress,
    market,
    expectedQuoteMint,
    ...(minimumContextSlot === undefined ? {} : { minimumContextSlot }),
  });
  return {
    market: marketAddress.toBase58(),
    chainSlot: pool.chainSlot.toString(),
    chainUnixTimestampSeconds: pool.chainUnixTimestampSeconds.toString(),
    lifecycle: 'graduated',
    winningOutcomeIndex: binding.outcomeIndex,
    bondingCurvesClosed: true,
    buyFeeBps: null,
    sellBaseFeeBps: null,
    sellPenaltyFeeBps: null,
    prizePoolFeeBps: null,
    minimumQuoteTradeD6: null,
    bondingCurveTradeGate: null,
    outcomes: Array.from({ length: market.nOutcomes }, (_, outcomeIndex) => ({
      outcomeIndex,
      outcomeMint: deriveOutcomeMintPda(marketAddress, outcomeIndex)[0].toBase58(),
      realQuoteReserveD6: null,
      bondingCurve: null,
      cpSwap: outcomeIndex === binding.outcomeIndex ? cpSwapSnapshot(pool) : null,
    })),
  };
}

function cpSwapSnapshot(
  info: Awaited<ReturnType<typeof fetchGraduatedPoolInfoByPool>>,
): CpSwapPoolSnapshot {
  const creatorFeeOn =
    info.creatorFeeOn === 0
      ? 'both'
      : info.creatorFeeOn === 1
        ? 'token0'
        : info.creatorFeeOn === 2
          ? 'token1'
          : null;
  if (creatorFeeOn === null) throw new Error('Unknown cp-swap creator-fee mode');
  return {
    pool: info.poolState.toBase58(),
    quoteReserveD6: info.usdcReserves.toString(),
    outcomeReserveD6: info.outcomeReserves.toString(),
    tradeFeeRateMillionths: info.tradeFeeRateMillionths.toString(),
    creatorFeeRateMillionths: info.creatorFeeRateMillionths.toString(),
    creatorFeeEnabled: info.creatorFeeEnabled,
    creatorFeeOn,
    outcomeTokenIndex: info.quoteTokenIndex === 0 ? 1 : 0,
    swapEnabled: info.swapEnabled,
    openTimeUnixSeconds: info.openTime.toString(),
  };
}

function marketLifecycle(market: MarketAccount): MarketLifecycle {
  if (hasVariant(market.state, 'resolutionPending')) return 'resolution_pending';
  for (const state of ['backing', 'backed', 'trading', 'resolved', 'graduated'] as const) {
    if (hasVariant(market.state, state)) return state;
  }
  throw new Error('Unknown market lifecycle variant');
}

function accountInfoAt(
  accounts: readonly (AccountInfo<Buffer> | null)[],
  index: number,
  label: string,
): AccountInfo<Buffer> {
  const account = accounts[index];
  if (account === undefined || account === null)
    throw new Error(`${label} is missing from atomic snapshot`);
  return account;
}
